/**
 * #88 — manually add a CUEC the AI missed. A partial/none-coverage applicable
 * CUEC opens its A19 server-side, so it behaves like an AI-extracted one.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  Toggle,
  type SelectProps,
} from "@cloudscape-design/components";
import { createCuec } from "./api";

const COVERAGE: SelectProps.Option[] = [
  { label: "None — no RCM coverage", value: "none" },
  { label: "Partial — gaps remain", value: "partial" },
  { label: "Full — RCM fully covers", value: "full" },
  { label: "N/A — not applicable", value: "na" },
];

export function AddCuecModal({
  reportId,
  visible,
  onDismiss,
  onAdded,
}: {
  reportId: string;
  visible: boolean;
  onDismiss: () => void;
  onAdded: () => void;
}) {
  const [text, setText] = useState("");
  const [applicable, setApplicable] = useState(true);
  const [coverage, setCoverage] = useState("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await createCuec(reportId, { text, applicable, coverage });
      setText("");
      setApplicable(true);
      setCoverage("none");
      onAdded();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Add CUEC"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={busy || !text.trim()} loading={busy}>
              Add CUEC
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        {error && (
          <Alert type="error" header="Add failed">
            {error}
          </Alert>
        )}
        <FormField label="CUEC text">
          <Textarea
            value={text}
            onChange={({ detail }) => setText(detail.value)}
            rows={4}
            placeholder="The user entity is responsible for…"
          />
        </FormField>
        <FormField label="Applicable to your organization?">
          <Toggle checked={applicable} onChange={({ detail }) => setApplicable(detail.checked)}>
            {applicable ? "Yes" : "No"}
          </Toggle>
        </FormField>
        <FormField label="Coverage">
          <Select
            selectedOption={COVERAGE.find((o) => o.value === coverage) ?? COVERAGE[0]!}
            onChange={({ detail }) => setCoverage(detail.selectedOption.value ?? "none")}
            options={COVERAGE}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
